import { useState, useEffect } from 'react';
import { orderApi, OrderStatus } from '../api/orderApi';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import OrderStatusBadge from '../components/OrderStatusBadge';

interface OrderItem {
  id: number;
  orderId: number;
  name: string;
  code: string;
  brand: string;
  count: number;
  pricePerUnit: number;
  basePricePerUnit: number;
  totalPrice: number;
  deliveryDate: string;
}

interface Order {
  id: number;
  userId?: string;
  anonymousId?: string;
  fio: string;
  phone: string;
  email?: string | null;
  deliveryAddress?: string | null;
  comment?: string | null;
  totalPrice: number;
  created: string;
  status: OrderStatus;
  items: OrderItem[];
}

export default function AccountOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!isAuthenticated) {
          navigate('/login');
          return;
        }
        
        const response = await orderApi.getUserOrders();
        if (response.success && response.data) {
          setOrders(response.data);
        } else {
          setError(response.message || 'Ошибка при загрузке заказов');
        }
      } catch (err) {
        setError('Ошибка при загрузке заказов');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, navigate]);

  if (loading) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col gap-6 py-10">
        <h1 className="text-2xl font-semibold">Мои заказы</h1>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">У вас пока нет заказов</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-0 flex-1 flex-col gap-4 md:gap-6 py-6 md:py-10 px-4 md:px-0">
      <h1 className="text-xl md:text-2xl font-semibold">Мои заказы</h1>
      
      <div className="space-y-4 md:space-y-6">
        {orders.map((order) => (
          <div key={order.id} className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="mb-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                <div className="flex-1">
                  <h2 className="text-base md:text-lg font-medium mt-0 mb-2">
                    Заказ #{order.id}
                    <span className="pl-2 text-xs text-gray-500 font-normal">
                      от {new Date(order.created).toLocaleDateString('ru-RU')}
                    </span>
                  </h2>
                  
                  <OrderStatusBadge status={order.status} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-2">Товары:</h3>
              <div className="space-y-2">
                {order.items.map((item) => (
                  <div key={item.id} className="text-xs md:text-sm flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0 p-2 bg-gray-50 rounded">
                    <span className="pr-2 break-words">{item.brand} {item.name} ({item.code})</span>
                    <span className="whitespace-nowrap">
                      {item.count} × {item.pricePerUnit} ₽ = {item.totalPrice} ₽
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-base md:text-lg font-medium">Итого:</span>
                  <span className="text-base md:text-lg font-medium">{order.totalPrice} ₽</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 